import React, { useMemo, useState } from 'react'

import { useQueryStation } from '@/api/stations/queries'

import ConnectorIcons from '@/components/ConnectorIcons'
import PageHeader from '@/components/PageHeader'
import { PopoverTooltip } from '@/components/Tooltip/PopoverTooltip'
import { TariffAlert, TARIFF_ALERT_HINT } from '@/components/TariffAlert'

import { Capacitor } from '@capacitor/core'
import { AppLauncher } from '@capacitor/app-launcher'

import { useParams, useNavigate } from 'react-router-dom'

import { Stack, Box, Chip, Typography, Button, IconButton, Skeleton, Divider } from '@mui/material'
import {
  LocalPhoneOutlined,
  EmailOutlined,
  NearMeOutlined,
  EvStationOutlined,
  PlaceOutlined,
  BoltOutlined,
  AttachMoneyOutlined,
  MoreTimeOutlined,
  ElectricalServicesOutlined,
  PaidOutlined,
  InfoOutlined,
} from '@mui/icons-material'

import { useBackButton } from '@/hooks/useBackButton'
import { useGeolocationContext } from '@/contexts/GeolocationContext'

import { chargingPointStatusUtils } from '@/utils/chargingPointStatus'
import { connectorUtils } from '@/utils/connector'
import { numericUtils } from '@/utils/numeric'
import { tariffUtils } from '@/utils/tariff'
import { timeUtils } from '@/utils/time'
import { haversineDistance } from '@/utils/map'

import { calcWithSafeAreaBottom } from '@/utils/layout'

import { actionButtonSx } from '@/theme'

import { AppRoutes } from '@/routes/types/routes'
import { routeParser } from '@/routes/helpers/parser'

const HERO_MIN_HEIGHT = 96
const HERO_MAX_HEIGHT = 200

const SECTION_GAP = 'clamp(8px, 1.6vh, 16px)'
const CARD_PADDING = 'clamp(10px, 1.6vh, 16px)'
const CARD_ROW_GAP = 'clamp(10px, 1.6vh, 16px)'

const TITLE_FONT_SIZE = 'clamp(1.05rem, 4.8vw, 1.25rem)'
const BODY_FONT_SIZE = 'clamp(0.75rem, 3.2vw, 0.875rem)'
const SPEC_LABEL_FONT_SIZE = 'clamp(0.625rem, 2.7vw, 0.6875rem)'
const SPEC_VALUE_FONT_SIZE = 'clamp(0.8125rem, 3.5vw, 0.9375rem)'
const SPEC_ICON_SIZE = 14
const HEADER_ALIGN_OFFSET = `calc(1px + ${CARD_PADDING})`
const SPEC_HALF_WIDTH_MAX_LENGTH = 18
const CTA_FONT_SIZE = 'clamp(0.875rem, 3.8vw, 0.9375rem)'

const StationPage: React.FC = () => {
  useBackButton({ backRoute: AppRoutes.STATIONS })

  const params = useParams()
  const navigate = useNavigate()

  const { coords } = useGeolocationContext()

  const [hasImageError, setHasImageError] = useState(false)

  const stationId = params.stationId as string

  const { data: station, isLoading: isStationLoading } = useQueryStation(
    {
      id: Number(stationId),
    },
    {
      enabled: Boolean(stationId),
      refetchInterval: 5000,
      retry: false,
    }
  )

  const statusChipColor = station ? chargingPointStatusUtils.getChipColor(station.status) : undefined
  const statusLabel = station ? chargingPointStatusUtils.getLabel(station.status) : 'Desconhecido'

  const chargingPoints = station?.charging_points ?? []
  const availableChargingPointsCount = chargingPoints.filter(
    (chargingPoint) => chargingPointStatusUtils.getBucket(chargingPoint.status) === 'AVAILABLE'
  ).length
  const showsAvailableCount = station?.status === 'AVAILABLE' && chargingPoints.length > 1

  const distance = useMemo(() => {
    if (!coords || !station) return null

    return haversineDistance(coords, { lat: station.latitude, lng: station.longitude }) / 1000
  }, [coords, station])

  const hasDynamicTariff = chargingPoints.some((chargingPoint) =>
    chargingPoint.tariff_week_timeline?.length
      ? tariffUtils.hasDynamicTariffInWeek(chargingPoint.tariff_week_timeline)
      : tariffUtils.hasDynamicTariff(chargingPoint.tariff_timeline)
  )

  const connectorTypes = Array.from(
    new Set(
      chargingPoints.flatMap((chargingPoint) =>
        chargingPoint.charging_connectors.map((connector) => connector.connector_type)
      )
    )
  )

  const powers = Array.from(new Set(chargingPoints.map((chargingPoint) => chargingPoint.power)))

  const rates = Array.from(
    new Set(chargingPoints.map((chargingPoint) => chargingPoint.current_rate_per_kwh ?? chargingPoint.rate_per_kwh))
  ).filter((rate): rate is number => rate != null)

  const isFree = chargingPoints.length > 0 && chargingPoints.every((chargingPoint) => chargingPoint.is_free)

  const activationFees = Array.from(
    new Set(chargingPoints.map((chargingPoint) => chargingPoint.current_activation_fee ?? chargingPoint.activation_fee))
  )
  const hasActivationFee = activationFees.some((fee) => fee > 0)

  const idleRates = Array.from(
    new Set(chargingPoints.map((chargingPoint) => chargingPoint.current_idle_rate ?? chargingPoint.idle_rate))
  )
  const hasIdleRate = idleRates.some((rate) => rate > 0)
  const hasIdleSchedule = chargingPoints.some((chargingPoint) =>
    tariffUtils.hasVaryingRates(chargingPoint.idle_timeline)
  )
  const gracePeriods = Array.from(
    new Set(chargingPoints.map((chargingPoint) => chargingPoint.idle_grace_period_minutes))
  ).filter((minutes) => typeof minutes === 'number')
  const graceLabel = numericUtils.formatDistinctValues(
    gracePeriods.filter((minutes) => minutes > 0),
    (minutes) => timeUtils.formatMinutesToTime(minutes)
  )
  const graceNote = graceLabel ? `Tolerância de ${graceLabel}` : 'Sem tolerância'

  const connectorsValue = connectorTypes.map(connectorUtils.getLabel).join(', ') || 'Desconhecido'

  const specs: {
    key: string
    icon: React.ReactNode
    label: string
    value: string
    note?: string
    hint?: string
    fullWidth?: boolean
  }[] = [
    {
      key: 'chargers',
      icon: <EvStationOutlined sx={{ fontSize: SPEC_ICON_SIZE }} />,
      label: chargingPoints.length > 1 ? 'Carregadores' : 'Carregador',
      value: String(chargingPoints.length),
    },
    {
      key: 'power',
      icon: <BoltOutlined sx={{ fontSize: SPEC_ICON_SIZE }} />,
      label: 'Potência',
      value: numericUtils.formatDistinctValues(powers, (power) => `${power} kW`) || 'Desconhecida',
    },
    {
      key: 'rate',
      icon: <AttachMoneyOutlined sx={{ fontSize: SPEC_ICON_SIZE }} />,
      label: 'Preço (kWh)',
      value: isFree
        ? 'Gratuito'
        : numericUtils.formatDistinctValues(rates, (rate) => numericUtils.formatNumberToBRLCurrency(rate)) ||
          'Desconhecido',
    },
    {
      key: 'connectors',
      icon: <ElectricalServicesOutlined sx={{ fontSize: SPEC_ICON_SIZE }} />,
      label: connectorTypes.length > 1 ? 'Conectores' : 'Conector',
      value: connectorsValue,
      fullWidth: connectorsValue.length > SPEC_HALF_WIDTH_MAX_LENGTH,
    },
    ...(hasActivationFee
      ? [
          {
            key: 'activation',
            icon: <PaidOutlined sx={{ fontSize: SPEC_ICON_SIZE }} />,
            label: 'Ativação',
            value:
              numericUtils.formatDistinctValues(activationFees, (fee) => numericUtils.formatNumberToBRLCurrency(fee)) ||
              'Desconhecida',
            hint: 'Valor fixo cobrado uma vez a cada recarga, somado ao que você gastar de energia.',
          },
        ]
      : []),
    ...(hasIdleRate
      ? [
          {
            key: 'idle',
            icon: <MoreTimeOutlined sx={{ fontSize: SPEC_ICON_SIZE }} />,
            label: 'Ociosidade',
            value:
              numericUtils.formatDistinctValues(idleRates, (rate) => numericUtils.formatNumberToBRLCurrency(rate)) ||
              'Desconhecida',
            note: gracePeriods.length > 0 ? graceNote : undefined,
            hint: hasIdleSchedule
              ? 'Valor por minuto cobrado se o veículo continuar conectado depois que a carga termina, passado o tempo de tolerância. Nesta estação ele muda conforme o horário, confira a grade na tela do carregador.'
              : 'Valor por minuto cobrado se o veículo continuar conectado depois que a carga termina, passado o tempo de tolerância.',
          },
        ]
      : []),
  ]

  const connectorsSpec = specs.find((spec) => spec.key === 'connectors')
  const orderedSpecs = connectorsSpec?.fullWidth
    ? [...specs.slice(0, 2), connectorsSpec, ...specs.slice(2).filter((spec) => spec !== connectorsSpec)]
    : specs

  let currentColumn = 0
  const layoutSpecs = orderedSpecs.map((spec, index) => {
    const isFullWidth = Boolean(spec.fullWidth) || (index === orderedSpecs.length - 1 && currentColumn === 0)
    currentColumn = isFullWidth ? 0 : (currentColumn + 1) % 2

    return { ...spec, fullWidth: isFullWidth }
  })

  const hasImage = Boolean(station?.image) && !hasImageError

  const locationLine = [station?.address, station?.city && `${station.city} - ${station.state}`]
    .filter(Boolean)
    .join(' · ')

  const handleOnClickTraceRoute = async () => {
    const destination = `${station?.latitude},${station?.longitude}`

    let url: string
    if (Capacitor.getPlatform() === 'ios') {
      url = `maps://?daddr=${destination}`
    } else if (Capacitor.getPlatform() === 'android') {
      url = `geo:${destination}?q=${destination}`
    } else {
      url = `https://www.google.com/maps/dir/?api=1&destination=${destination}`
    }

    try {
      await AppLauncher.openUrl({ url })
    } catch (_error) {
      window.open(url, '_blank')
    }
  }

  const handleOnClickStartCharging = () => {
    navigate(routeParser(AppRoutes.STATION_CHARGERS, { stationId }))
  }

  const contactButtonSx = {
    width: 44,
    height: 44,
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 4,
    color: 'primary.main',
  }

  return (
    <Stack
      paddingX="24px"
      sx={{
        height: 'calc(100vh - 112px - env(safe-area-inset-bottom))',
        paddingTop: 'calc(24px + env(safe-area-inset-top))',
        paddingBottom: calcWithSafeAreaBottom('24px'),
        gap: SECTION_GAP,
      }}
    >
      <Stack sx={{ flexShrink: 0, gap: SECTION_GAP }}>
        <PageHeader title="Detalhes da estação" backRoute={AppRoutes.STATIONS} />
        <Divider />
      </Stack>

      <Stack sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', gap: SECTION_GAP }}>
        {isStationLoading ? (
          <Skeleton
            variant="rectangular"
            width="100%"
            sx={{ flex: '1 1 auto', minHeight: HERO_MIN_HEIGHT, maxHeight: HERO_MAX_HEIGHT, borderRadius: 4 }}
          />
        ) : (
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              flex: '1 1 auto',
              minHeight: HERO_MIN_HEIGHT,
              maxHeight: HERO_MAX_HEIGHT,
              borderRadius: 4,
              overflow: 'hidden',
              display: 'grid',
              placeItems: 'center',
              backgroundColor: hasImage ? 'grey.100' : 'background.paper',
              ...(!hasImage && { border: '1px dashed', borderColor: 'divider' }),
            }}
          >
            {hasImage ? (
              <Box
                component="img"
                src={station?.image ?? ''}
                alt={station?.name}
                onError={() => setHasImageError(true)}
                sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <Stack alignItems="center" spacing={1}>
                <EvStationOutlined sx={{ fontSize: 44, color: 'grey.400' }} />
                <Typography color="text.disabled" sx={{ fontSize: BODY_FONT_SIZE }}>
                  Sem foto da estação
                </Typography>
              </Stack>
            )}

            {hasDynamicTariff && (
              <Box sx={{ position: 'absolute', top: 8, right: 8, maxWidth: 'calc(100% - 16px)' }}>
                <PopoverTooltip content={TARIFF_ALERT_HINT}>
                  <TariffAlert opaque />
                </PopoverTooltip>
              </Box>
            )}
          </Box>
        )}

        <Stack sx={{ flexShrink: 0, gap: 0.5, paddingLeft: HEADER_ALIGN_OFFSET }}>
          {isStationLoading ? (
            <Skeleton variant="text" width="80%" height={28} />
          ) : (
            <Typography
              fontWeight="bold"
              sx={{
                fontSize: TITLE_FONT_SIZE,
                lineHeight: 1.3,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {station?.name}
            </Typography>
          )}
          <Stack direction="row" alignItems="flex-start" spacing={0.5} sx={{ color: 'text.secondary' }}>
            <PlaceOutlined sx={{ fontSize: 18, marginTop: '1px', flexShrink: 0 }} />
            <Typography
              sx={{
                fontSize: BODY_FONT_SIZE,
                lineHeight: 1.35,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {locationLine}
            </Typography>
          </Stack>
        </Stack>

        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
          <Button
            variant="outlined"
            color="primary"
            disableElevation
            startIcon={<NearMeOutlined />}
            onClick={handleOnClickTraceRoute}
            sx={{ flexGrow: 1, borderRadius: 4, textTransform: 'none', fontSize: BODY_FONT_SIZE }}
          >
            Traçar rota
          </Button>
          {station?.phone && (
            <IconButton
              component="a"
              href={`tel:${station.phone}`}
              aria-label="Ligar para a estação"
              sx={contactButtonSx}
            >
              <LocalPhoneOutlined fontSize="small" />
            </IconButton>
          )}
          {station?.email && (
            <IconButton
              component="a"
              href={`mailto:${station.email}`}
              aria-label="Enviar e-mail para a estação"
              sx={contactButtonSx}
            >
              <EmailOutlined fontSize="small" />
            </IconButton>
          )}
        </Stack>

        {!isStationLoading && (
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ flexShrink: 0 }}>
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
              <Chip
                label={
                  showsAvailableCount
                    ? `${statusLabel} · ${availableChargingPointsCount}/${chargingPoints.length}`
                    : statusLabel
                }
                size="small"
                color={statusChipColor}
                sx={{ fontWeight: 700, flexShrink: 0 }}
              />
              {distance !== null && (
                <Chip
                  icon={<NearMeOutlined />}
                  label={`${numericUtils.formatNumberToBRLDecimal(distance)} km`}
                  size="small"
                  variant="outlined"
                  sx={{ fontWeight: 600, flexShrink: 0 }}
                />
              )}
            </Stack>
            <ConnectorIcons connectorTypes={connectorTypes} />
          </Stack>
        )}

        {!isStationLoading && chargingPoints.length > 0 && (
          <Box
            sx={{
              flexShrink: 0,
              display: 'grid',
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
              columnGap: 2,
              rowGap: CARD_ROW_GAP,
              alignItems: 'start',
              padding: CARD_PADDING,
              borderRadius: 4,
              border: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'background.paper',
            }}
          >
            {layoutSpecs.map((spec) => (
              <Box
                key={spec.key}
                sx={{
                  minWidth: 0,
                  gridColumn: spec.fullWidth ? '1 / -1' : undefined,
                  display: 'grid',
                  gridTemplateColumns: `${SPEC_ICON_SIZE}px minmax(0, 1fr)`,
                  gridAutoRows: 'min-content',
                  columnGap: 0.375,
                  rowGap: 0.25,
                  alignContent: 'start',
                  alignItems: 'center',
                }}
              >
                <Box sx={{ display: 'flex', color: 'primary.main' }}>{spec.icon}</Box>
                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
                  <Typography
                    color="text.secondary"
                    noWrap
                    sx={{
                      fontSize: SPEC_LABEL_FONT_SIZE,
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      fontWeight: 600,
                      minWidth: 0,
                    }}
                  >
                    {spec.label}
                  </Typography>
                  {spec.hint && (
                    <PopoverTooltip content={spec.hint}>
                      <InfoOutlined sx={{ fontSize: 14, color: 'primary.main', flexShrink: 0 }} />
                    </PopoverTooltip>
                  )}
                </Stack>
                <Typography
                  fontWeight={700}
                  sx={{
                    gridColumn: 2,
                    fontSize: SPEC_VALUE_FONT_SIZE,
                    lineHeight: 1.35,
                    overflowWrap: 'anywhere',
                  }}
                >
                  {spec.value}
                </Typography>
                {spec.note && (
                  <Typography
                    color="text.secondary"
                    noWrap
                    sx={{ gridColumn: 2, fontSize: SPEC_LABEL_FONT_SIZE, lineHeight: 1.3 }}
                  >
                    {spec.note}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        )}
      </Stack>

      <Stack sx={{ flexShrink: 0 }}>
        {isStationLoading ? (
          <Skeleton variant="rounded" width="100%" height={48} sx={{ borderRadius: 999 }} />
        ) : chargingPoints.length > 0 ? (
          <Button
            variant="contained"
            color="primary"
            disableElevation
            fullWidth
            onClick={handleOnClickStartCharging}
            sx={{ ...actionButtonSx, fontSize: CTA_FONT_SIZE }}
          >
            Ver carregadores
          </Button>
        ) : (
          <Typography color="text.secondary" textAlign="center" sx={{ fontSize: BODY_FONT_SIZE, paddingY: 1.5 }}>
            Nenhum carregador cadastrado nesta estação.
          </Typography>
        )}
      </Stack>
    </Stack>
  )
}

export default StationPage
